import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import { useT } from '../../i18n/LanguageContext';
import './CashBank.css';

const integer = (value) => Number.isSafeInteger(value) && value >= 0;
const historyKeys = [
  'bankentries',
  'bankoperations',
  'distributorpurchases',
  'orders',
  'distributorwholesaleaccountingreceipts',
  'distributorcashdeposits',
];
const allowedAdmin = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return (
      user?.role === 'admin' &&
      user.isActive !== false &&
      !user.disabled &&
      !user.distributorPreview &&
      !user.distributorPreviewExpiresAt
    );
  } catch {
    return false;
  }
};
export const readActivationPreflight = (response, distributorId) => {
  const row = response?.data?.data;
  if (
    !row ||
    row.schemaVersion !== 1 ||
    row.distributorId !== distributorId ||
    !integer(row.revision) ||
    typeof row.accountEnabled !== 'boolean' ||
    typeof row.canActivateZero !== 'boolean' ||
    row.fundingAvailable !== false ||
    !row.counts ||
    typeof row.counts !== 'object' ||
    Array.isArray(row.counts) ||
    Object.keys(row.counts).length !== historyKeys.length ||
    !historyKeys.every((key) => integer(row.counts[key])) ||
    (row.canActivateZero &&
      (row.accountEnabled || !Object.values(row.counts).every((count) => count === 0)))
  )
    throw new Error('Invalid bank activation status');
  return row;
};

/** Enables an already saved empty bank at exactly zero. This cannot enable payments or add credit. */
export default function CashBankActivation({
  distributorId,
  disabled = false,
  onLockChange,
  onUpdated,
}) {
  const t = useT();
  const allowed = allowedAdmin() && /^[a-f\d]{24}$/.test(distributorId || '');
  const [state, setState] = useState(null),
    [loadedVersion, setLoadedVersion] = useState('');
  const [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false),
    [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [reload, setReload] = useState(0);
  const saving = useRef(false),
    unconfirmed = useRef(false),
    callbacks = useRef({ onLockChange, onUpdated });
  callbacks.current = { onLockChange, onUpdated };
  const { begin, version } = useShippingRequest(`cash-bank-activation:${distributorId}`);
  useEffect(() => {
    callbacks.current.onLockChange?.(busy || uncertain);
    return () => callbacks.current.onLockChange?.(false);
  }, [busy, uncertain]);
  useEffect(() => {
    saving.current = false;
    unconfirmed.current = false;
    setUncertain(false);
    setNotice('');
    setConfirmed(false);
  }, [distributorId, version]);
  useEffect(() => {
    const request = begin();
    setState(null);
    setConfirmed(false);
    setError('');
    setBusy(allowed);
    if (!allowed) return;
    Promise.resolve(distributorService.cashBankPreflight(distributorId, { signal: request.signal }))
      .then((response) => {
        if (!request.isCurrent()) return;
        const row = readActivationPreflight(response, distributorId);
        setState(row);
        setLoadedVersion(version);
        setUncertain(false);
        if (unconfirmed.current && row.accountEnabled) {
          unconfirmed.current = false;
          setNotice('This bank is active. No additional activation request was sent.');
          callbacks.current.onUpdated?.();
        }
      })
      .catch(() => {
        if (request.isCurrent())
          setError('Bank activation status could not load. Reload before continuing.');
      })
      .finally(() => {
        if (request.isCurrent()) setBusy(false);
      });
  }, [allowed, begin, version, distributorId, reload]);
  const activate = async (event) => {
    event.preventDefault();
    if (
      !allowedAdmin() ||
      disabled ||
      busy ||
      uncertain ||
      saving.current ||
      !confirmed ||
      !state?.canActivateZero ||
      state.distributorId !== distributorId ||
      loadedVersion !== version
    )
      return;
    const request = begin();
    saving.current = true;
    unconfirmed.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    setConfirmed(false);
    try {
      await distributorService.activateEmptyCashBank(distributorId, {
        revision: state.revision,
        confirmedZeroHistory: true,
      });
      if (!request.isCurrent()) return;
      const after = readActivationPreflight(
        await distributorService.cashBankPreflight(distributorId, { signal: request.signal }),
        distributorId
      );
      if (!request.isCurrent()) return;
      if (!after.accountEnabled || after.canActivateZero || after.revision <= state.revision)
        throw new Error('Activation not confirmed');
      setState(after);
      setLoadedVersion(version);
      unconfirmed.current = false;
      setNotice('Empty bank activated at $0.00. Payments remain separately controlled.');
      callbacks.current.onUpdated?.();
    } catch {
      if (request.isCurrent()) {
        setUncertain(true);
        setError('Activation could not be confirmed. Reload its current status before continuing.');
      }
    } finally {
      if (request.isCurrent()) {
        saving.current = false;
        setBusy(false);
      }
    }
  };
  if (!allowed) return null;
  const current =
    state?.distributorId === distributorId && loadedVersion === version ? state : null;
  if (!busy && !error && !notice && current && !current.canActivateZero) return null;
  return (
    <fieldset className="me-cash me-cash-card">
      <legend>{t('Empty bank activation')}</legend>
      {busy && <p role="status">{t('Checking bank activation...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {current?.canActivateZero && (
        <form onSubmit={activate}>
          <p>
            {t(
              'Enable this saved Virtual Tissue Bank at $0.00. This does not add funds or enable payments.'
            )}
          </p>
          <fieldset disabled={disabled || busy || uncertain}>
            <label className="me-cash-authorize">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              <span>
                {t(
                  'I reviewed this distributor and confirm its bank and transaction history are empty.'
                )}
              </span>
            </label>
            <button type="submit" disabled={!confirmed}>
              {t('Activate empty bank')}
            </button>
          </fieldset>
        </form>
      )}
      {(error || uncertain) && (
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => setReload((value) => value + 1)}
        >
          {t('Reload activation status')}
        </button>
      )}
    </fieldset>
  );
}
